import { useState, useEffect } from "react";

/**
 * Custom hook for implementing type-ahead functionality.
 *
 * Features:
 * - Accumulates typed characters with automatic timeout clearing
 * - Provides type-ahead string for keyboard navigation
 * - Automatically resets after specified timeout period
 * - Supports customizable timeout duration
 * - Uses useEffect for automatic cleanup
 * - Optimized for keyboard navigation in lists and menus
 *
 * @param timeout - Timeout duration in milliseconds before clearing the type-ahead string
 * @returns Tuple containing current type-ahead string and function to update it
 */
export const useTypeAhead = (
  timeout = 750,
): [string, (char: string) => void] => {
  const [typeAhead, setTypeAhead] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setTypeAhead(""), timeout);
    return () => clearTimeout(timer);
  }, [typeAhead, timeout]);

  const updateTypeAhead = (char: string) => setTypeAhead((prev) => prev + char);

  return [typeAhead, updateTypeAhead];
};
